import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from '@/components/theme-provider'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { BottomNav } from '@/components/bottom-nav'
import { SearchHeader } from '@/components/search-header'
import { ScrollRestoration } from '@/components/ScrollRestoration'
import { PageTransition } from '@/components/PageTransition'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { GuestRoute } from '@/components/GuestRoute'
import { InstallPrompt } from '@/components/InstallPrompt'
import HomePage from '@/pages/HomePage'

/**
 * Semua halaman selain beranda dimuat saat dibutuhkan.
 *
 * Sebelumnya SATU berkas 1 MB (254 KB terkompresi) memuat seluruh aplikasi —
 * termasuk panel admin lengkap — dan harus selesai diunduh serta di-parse
 * sebelum piksel pertama muncul. Di jaringan 4G lambat itu berarti LCP 8 detik
 * untuk pengunjung yang cuma mau membaca satu halaman penyakit.
 *
 * Beranda sengaja TIDAK di-lazy: dia yang paling sering jadi halaman pertama,
 * jadi memisahnya justru menambah satu perjalanan bolak-balik.
 */
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const PanduanPage = lazy(() => import('@/pages/PanduanPage'))
const PanduanDetailPage = lazy(() => import('@/pages/PanduanDetailPage'))
const KategoriPage = lazy(() => import('@/pages/KategoriPage'))
const JenisAyamPage = lazy(() => import('@/pages/JenisAyamPage'))
const ChickenBreedDetailPage = lazy(() => import('@/pages/ChickenBreedDetailPage'))
const PenyakitPage = lazy(() => import('@/pages/PenyakitPage'))
const DiseaseDetailPage = lazy(() => import('@/pages/DiseaseDetailPage'))
const VaksinasiPage = lazy(() => import('@/pages/VaksinasiPage'))
const VaccineDetailPage = lazy(() => import('@/pages/VaccineDetailPage'))
const FeedCalculatorPage = lazy(() => import('@/pages/FeedCalculatorPage'))
const RacikanPage = lazy(() => import('@/pages/RacikanPage'))
const PenipuPage = lazy(() => import('@/pages/PenipuPage'))
const PenipuDetailPage = lazy(() => import('@/pages/PenipuDetailPage'))
const EtalasePage = lazy(() => import('@/pages/EtalasePage'))
const ProductDetailPage = lazy(() => import('@/pages/ProductDetailPage'))
const MenuPage = lazy(() => import('@/pages/MenuPage'))
const FAQPage = lazy(() => import('@/pages/FAQPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'))
const TermsConditionsPage = lazy(() => import('@/pages/TermsConditionsPage'))
const TentangPage = lazy(() => import('@/pages/TentangPage'))
const TrendingDetailPage = lazy(() => import('@/pages/TrendingDetailPage'))
const HargaKomoditasPage = lazy(() => import('@/pages/HargaKomoditasPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))

// Panel admin: dipakai segelintir orang, tapi paling berat isinya.
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })))
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'))
const AdminEtalasePage = lazy(() => import('@/pages/admin/AdminEtalasePage'))
const AdminLibraryPage = lazy(() => import('@/pages/admin/AdminLibraryPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminHargaPage = lazy(() => import('@/pages/admin/AdminHargaPage'))
const AdminAktivitasPage = lazy(() => import('@/pages/admin/AdminAktivitasPage'))
const AdminBacklogPage = lazy(() => import('@/pages/admin/AdminBacklogPage'))

import { Toaster } from '@/components/ui/sonner'
import { usePageTracking } from '@/services/track'
import { AdminRoute } from '@/components/admin/AdminRoute'

/** Penanda antar-halaman. Tingginya dikunci agar tidak menggeser tata letak. */
function MuatHalaman() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}

const AUTH_PATHS = ['/login', '/register', '/mulai', '/verify-email', '/forgot-password', '/reset-password']

function AppContent() {
  const location = useLocation()
  const { user, isLoading } = useAuth()
  usePageTracking(!isLoading && user?.role !== 'administrator')

  // Area admin: full-width dengan sidebar sendiri, di luar shell mobile 480px
  if (location.pathname.startsWith('/admin')) {
    return (
      <>
        <Suspense fallback={<MuatHalaman />}>
        <Routes location={location}>
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/etalase" element={<AdminEtalasePage />} />
              <Route path="/admin/library/:section" element={<AdminLibraryPage />} />
              <Route path="/admin/harga" element={<AdminHargaPage />} />
              <Route path="/admin/aktivitas" element={<AdminAktivitasPage />} />
              <Route path="/admin/backlog" element={<AdminBacklogPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              {/* Fallback: route admin tak dikenal → pesan jelas, bukan layar kosong */}
              <Route
                path="/admin/*"
                element={
                  <div className="flex flex-col items-center justify-center py-24 text-center gap-2">
                    <h2 className="text-xl font-black">Halaman admin tidak ditemukan</h2>
                    <p className="text-sm text-muted-foreground">
                      Menu ini mungkin belum tersedia di versi yang ter-deploy.
                    </p>
                    <a href="/admin" className="text-primary font-bold hover:underline">Kembali ke Dashboard</a>
                  </div>
                }
              />
            </Route>
          </Route>
        </Routes>
        </Suspense>
        <Toaster position="top-center" />
      </>
    )
  }

  const isAuthPage = AUTH_PATHS.includes(location.pathname)
  const hideBottomNav = isAuthPage ||
                        location.pathname.match(/^\/etalase\/\d+$/) ||
                        location.pathname.match(/^\/jenis-ayam\/.+$/) ||
                        location.pathname.match(/^\/penyakit\/.+$/) ||
                        (location.pathname.match(/^\/panduan\/.+$/) && location.pathname !== '/panduan/racikan') ||
                        location.pathname.match(/^\/vaksinasi\/.+$/) ||
                        location.pathname.match(/^\/trending\/.+$/) ||
                        location.pathname === '/faq' ||
                        location.pathname === '/privacy-policy' ||
                        location.pathname === '/terms-conditions' ||
                        location.pathname.match(/^\/penipu\/.+$/)

  const showHeaderPaths = ['/', '/jenis-ayam', '/penyakit', '/vaksinasi', '/racik-pakan', '/panduan/racikan', '/penipu', '/kategori', '/menu', '/etalase']
  const showHeader = !isAuthPage && showHeaderPaths.includes(location.pathname)

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-950">
      <div className="relative mx-auto w-full max-w-[480px] min-h-screen bg-background shadow-[0_0_40px_rgba(0,0,0,0.08)] dark:shadow-[0_0_40px_rgba(0,0,0,0.3)]">
      <ScrollRestoration />
      {showHeader && <SearchHeader />}
      {/* Landmark <main>: pembaca layar butuh satu titik lompat ke isi utama,
          dan Lighthouse memeriksanya. Header pencarian serta bilah navigasi
          bawah sengaja berada DI LUAR — keduanya bukan isi halaman. */}
      <main id="isi">
      <PageTransition>
        <Suspense fallback={<MuatHalaman />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          {/* Etalase publik — hanya link Shopee, tak perlu login */}
          <Route path="/etalase" element={<EtalasePage />} />
          <Route path="/etalase/:id" element={<ProductDetailPage />} />

          {/* Semua halaman konten wajib login */}
          <Route element={<ProtectedRoute />}>
            {/* Pendataan wajib — penjaganya ada di ProtectedRoute */}
            <Route path="/mulai" element={<OnboardingPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/tentang" element={<TentangPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/panduan" element={<PanduanPage />} />
            <Route path="/panduan/racikan" element={<RacikanPage />} />
            <Route path="/panduan/:id" element={<PanduanDetailPage />} />
            <Route path="/kategori" element={<KategoriPage />} />
            <Route path="/jenis-ayam" element={<JenisAyamPage />} />
            <Route path="/jenis-ayam/:id" element={<ChickenBreedDetailPage />} />
            <Route path="/penyakit" element={<PenyakitPage />} />
            <Route path="/penyakit/:id" element={<DiseaseDetailPage />} />
            <Route path="/vaksinasi" element={<VaksinasiPage />} />
            <Route path="/vaksinasi/:id" element={<VaccineDetailPage />} />
            <Route path="/racik-pakan" element={<FeedCalculatorPage />} />
            <Route path="/penipu" element={<PenipuPage />} />
            <Route path="/penipu/:slug" element={<PenipuDetailPage />} />
            <Route path="/trending/:id" element={<TrendingDetailPage />} />
            <Route path="/harga-komoditas" element={<HargaKomoditasPage />} />
            <Route path="/profil" element={<ProfilePage />} />
          </Route>

          {/* Halaman auth: user yang sudah login dilempar ke tujuan/beranda */}
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
            {/* Pendaftaran terpisah sudah tidak ada: /login menangani keduanya. */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
          </Route>
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
        </Suspense>
      </PageTransition>
      </main>
      {!hideBottomNav && <BottomNav />}
      <InstallPrompt />
      <Toaster position="top-center" />
      </div>
    </div>
  )
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="light" storageKey="beternakayam-theme">
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
